import React, { useState, useEffect, useRef } from 'react';
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../config/firebase";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import OtpInput from 'react-otp-input';
import logo from '../Assets/Logo/logo.png';

const LoginModal = ({ isOpen, onClose, onSuccess }) => {
    const [step, setStep] = useState('phone'); // 'phone' or 'otp'
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [seconds, setSeconds] = useState(60);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isBlocked, setIsBlocked] = useState(false);
    const isLoggingIn = useRef(false);

    useEffect(() => {
        if (isOpen) {
            setStep('phone');
            setPhone('');
            setOtp('');
            setError('');
            setIsBlocked(false);
            setLoading(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (step === 'otp' && seconds > 0) {
            const timer = setTimeout(() => setSeconds((s) => s - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [step, seconds]);

    if (!isOpen) return null;

    const handlePhoneSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (phone.length < 10) {
            setError('Please enter a valid phone number.');
            return;
        }
        setLoading(true);
        try {
            const formatPhone = '+' + phone;
            const cleanApiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
            const response = await fetch(`${cleanApiBase}/auth/send-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ phone: formatPhone })
            });
            const data = await response.json();
            if (data.success) {
                setStep('otp');
                setSeconds(60);
            } else {
                if (data.is_blocked) {
                    setIsBlocked(true);
                    return;
                }
                throw new Error(data.message || 'Failed to send OTP');
            }
        } catch (error) {
            setError(error.message || 'Failed to send OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (otp.length === 4) {
            setLoading(true);
            try {
                const formatPhone = '+' + phone;
                const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
                const response = await fetch(`${apiBase}/auth/verify-otp`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({ phone: formatPhone, otp })
                });
                const data = await response.json();
                if (data.success && data.data?.token) {
                    localStorage.setItem('auth_token', data.data.token);
                    onSuccess();
                } else {
                    throw new Error(data.message || "Invalid OTP code");
                }
            } catch (err) {
                setError(err.message || 'Invalid OTP code. Please try again.');
            } finally {
                setLoading(false);
            }
        } else {
            setError('Please enter a 4-digit OTP.');
        }
    };

    const handleResend = async () => {
        try {
            setSeconds(60);
            setError('');
            const formatPhone = '+' + phone;
            const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
            const response = await fetch(`${apiBase}/auth/send-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ phone: formatPhone })
            });
            const data = await response.json();
            if (!data.success) {
                if (data.is_blocked) {
                    setIsBlocked(true);
                    return;
                }
                throw new Error(data.message || 'Failed to resend OTP');
            }
        } catch (err) {
            setError(err.message || 'Failed to resend OTP. Please try again.');
            setSeconds(0);
        }
    };

    const googleLogin = async () => {
        try {
            isLoggingIn.current = true;
            const result = await signInWithPopup(auth, googleProvider);
            const idToken = await result.user.getIdToken();
            try {
                const cleanApiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
                const response = await fetch(`${cleanApiBase}/auth/google-login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({ firebase_id_token: idToken })
                });
                const data = await response.json();
                if (data.success && data.data?.token) {
                    localStorage.setItem('auth_token', data.data.token);
                    onSuccess();
                } else {
                    if (data.is_blocked) {
                        setIsBlocked(true);
                        return;
                    }
                    throw new Error(data.message || "Backend login failed");
                }
            } catch (backendError) {
                throw backendError;
            }
        } catch (error) {
            setError(error.message || 'Google Login failed.');
            isLoggingIn.current = false;
            await auth.signOut();
        }
    };

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content" style={{ borderRadius: '1rem', border: 'none' }}>
                    <div className="modal-header border-0 pb-0">
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>
                    <div className="modal-body p-4 pt-0">
                        {isBlocked ? (
                            <div className="text-center py-4">
                                <div className="mb-4">
                                    <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '3rem' }}></i>
                                </div>
                                <h4 className="fw-bold text-dark mb-3">Account Suspended</h4>
                                <p className="text-muted mb-4">
                                    Oops! Your account has been suspended. Please reach out to our support team for assistance.
                                </p>
                                <div className="d-grid gap-3">
                                    <a href="mailto:contact@zoyotrip.com" className="btn btn-primary rounded-pill py-2">
                                        <i className="bi bi-envelope me-2"></i> Email contact@zoyotrip.com
                                    </a>
                                    <a href="tel:+919826178619" className="btn btn-outline-primary rounded-pill py-2">
                                        <i className="bi bi-telephone me-2"></i> Call +91 9826178619
                                    </a>
                                </div>
                                <button type="button" className="btn btn-link text-muted mt-3 text-decoration-none" onClick={() => setIsBlocked(false)}>
                                    Back to Login
                                </button>
                            </div>
                        ) : step === 'phone' ? (
                            <>
                                <div className="d-flex flex-column align-items-center mb-4">
                                    <img src={logo} alt="ZoyoTrip" width={120} className="mb-2" />
                                    <h5 className="mt-2 text-primary fw-bold">Welcome Back!</h5>
                                    <p className="text-muted small">Login with your phone number or google to continue</p>
                                </div>
                                {error && <div className="alert alert-danger small py-2">{error}</div>}
                                <form onSubmit={handlePhoneSubmit}>
                                    <div className="mb-4">
                                        <label className="form-label text-sm fw-bold">Mobile Number</label>
                                        <PhoneInput
                                            country={'in'}
                                            value={phone}
                                            onChange={phone => setPhone(phone)}
                                            inputClass="form-control w-100"
                                            containerClass="w-100"
                                            inputStyle={{ height: '45px', width: '100%' }}
                                        />
                                    </div>
                                    <div className="d-grid gap-2 mb-3">
                                        <button type="submit" className="btn btn-primary rounded-pill py-2" disabled={loading}>
                                            {loading ? 'Sending OTP...' : 'Continue with Phone'}
                                        </button>
                                    </div>
                                </form>
                                <div className="text-center my-3 text-muted"><small>OR</small></div>
                                <div className="d-grid gap-2">
                                    <button type="button" onClick={googleLogin} className="btn btn-outline-danger rounded-pill py-2 d-flex align-items-center justify-content-center gap-2">
                                        <i className="bi bi-google"></i> Continue with Google
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="d-flex flex-column align-items-center mb-4">
                                    <img src={logo} alt="ZoyoTrip" width="100" className="mb-2" />
                                    <h5 className="fw-bold mt-2">Verify Your Account</h5>
                                    <p className="small text-muted mb-1">Enter the 4-digit code sent to</p>
                                    <p className="small text-muted">
                                        <span className="fw-semibold text-dark">+{phone}</span>{' '}
                                        <button type="button" className="btn btn-sm btn-link text-primary text-decoration-none p-0 ms-2" onClick={() => setStep('phone')}>
                                            <i className="bi bi-pencil-square me-1"></i>Edit
                                        </button>
                                    </p>
                                </div>
                                {error && <div className="alert alert-danger small py-2 text-center">{error}</div>}
                                <form onSubmit={handleOtpSubmit}>
                                    <div className="d-flex justify-content-center mb-3">
                                        <OtpInput
                                            value={otp}
                                            onChange={setOtp}
                                            numInputs={4}
                                            shouldAutoFocus
                                            renderSeparator={<span> </span>}
                                            renderInput={(props) => <input {...props} />}
                                            inputStyle={{
                                                width: '3rem', height: '3rem', margin: '0 0.4rem', fontSize: '1.5rem',
                                                borderRadius: '0.5rem', border: '1px solid #ced4da', textAlign: 'center', backgroundColor: '#f8f9fa',
                                            }}
                                            focusStyle={{ border: '1px solid #007bff', outline: 'none' }}
                                        />
                                    </div>
                                    <div className="text-center mb-4">
                                        {seconds > 0 ? (
                                            <small className="text-muted">Resend OTP in <strong>{seconds}s</strong></small>
                                        ) : (
                                            <button type="button" className="btn btn-link text-primary p-0 small text-decoration-none" onClick={handleResend}>
                                                Resend OTP
                                            </button>
                                        )}
                                    </div>
                                    <div className="d-grid">
                                        <button type="submit" className="btn btn-primary rounded-pill py-2" disabled={loading}>
                                            {loading ? 'Verifying...' : 'Verify & Continue'}
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginModal;
